import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Eye, EyeOff, Loader2, ArrowLeft } from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';

const COLORS = {
  darkBlue: '#0a1628',
  mediumBlue: '#1a3a5c',
  cyan: '#00d4ff',
  red: '#dc2626',
  parchment: '#F5F5DC',
  white: '#ffffff',
  gray: '#9ca3af',
};

const LChaimLogo = () => (
  <h1 className="text-4xl md:text-5xl font-black tracking-tight">
    <span style={{ color: COLORS.white }}>L'</span>
    <span style={{ color: COLORS.cyan }}>CHA</span>
    <span style={{ color: COLORS.red }}>IM</span>
  </h1>
);

/**
 * Dedicated L'Chaim login page.
 * On success, ALWAYS routes to /academy/lchaim/dashboard — never bounces to
 * the Scholar Athlete cockpit (which is the bug the old shared login had).
 */
const LChaimLogin = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  // Auto-redirect if already a paid L'Chaim user
  useEffect(() => {
    const token = localStorage.getItem('academy_token');
    const userStr = localStorage.getItem('academy_user');
    if (token && userStr) {
      try {
        const user = JSON.parse(userStr);
        if (user.lchaim_tier) {
          navigate('/academy/lchaim/dashboard', { replace: true });
        }
      } catch (_e) {
        // ignore
      }
    }
  }, [navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Please enter your email and password.');
      return;
    }
    setLoading(true);
    try {
      const res = await api.post('/academy/auth/login', { email, password });
      if (!res.data?.success || !res.data?.token) {
        throw new Error('Login failed');
      }
      const user = res.data.user || {};
      localStorage.setItem('academy_token', res.data.token);
      localStorage.setItem('academy_user', JSON.stringify(user));

      // Smart routing: if user has a L'Chaim tier → L'Chaim dashboard
      //                if user has a school subscription → academy school
      //                else → enroll page (they haven't paid yet)
      if (user.lchaim_tier) {
        toast.success('Welcome back to L\'Chaim.');
        navigate('/academy/lchaim/dashboard', { replace: true });
      } else if (user.is_student || user.subscription_tier) {
        toast.success('Welcome back.');
        navigate('/academy/school', { replace: true });
      } else {
        toast.info('No active L\'Chaim subscription found. Choose a path to start.');
        navigate('/academy/lchaim', { replace: true });
      }
    } catch (err) {
      const msg = err.response?.data?.detail || 'Invalid email or password.';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-12" style={{ backgroundColor: COLORS.darkBlue }}>
      {/* Background grid */}
      <div
        className="fixed inset-0 pointer-events-none"
        style={{
          backgroundImage: `linear-gradient(${COLORS.cyan}08 1px, transparent 1px), linear-gradient(90deg, ${COLORS.cyan}08 1px, transparent 1px), radial-gradient(ellipse at 50% 0%, ${COLORS.mediumBlue}40 0%, transparent 70%)`,
          backgroundSize: '40px 40px, 40px 40px, 100% 100%',
        }}
      />

      <div className="relative z-10 w-full max-w-md">
        {/* Back to L'Chaim landing */}
        <Link
          to="/academy/lchaim"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-widest mb-8 hover:opacity-80"
          style={{ color: COLORS.cyan }}
          data-testid="back-to-landing"
        >
          <ArrowLeft className="w-4 h-4" /> Back to L'Chaim
        </Link>

        <div
          className="rounded-lg border p-8"
          style={{ backgroundColor: `${COLORS.mediumBlue}80`, borderColor: `${COLORS.cyan}40` }}
        >
          <div className="text-center mb-8">
            <LChaimLogo />
            <p className="mt-3 text-sm uppercase tracking-widest" style={{ color: COLORS.cyan }}>
              Member Sign In
            </p>
          </div>

          <form onSubmit={handleLogin} className="space-y-5" data-testid="lchaim-login-form">
            <div>
              <Label className="text-xs uppercase tracking-wider" style={{ color: COLORS.cyan }}>
                Email
              </Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="mt-1 border-0"
                style={{ backgroundColor: COLORS.parchment, color: COLORS.darkBlue }}
                data-testid="lchaim-login-email"
                autoFocus
              />
            </div>

            <div>
              <div className="flex items-center justify-between mt-1 mb-1">
                <Label className="text-xs uppercase tracking-wider" style={{ color: COLORS.cyan }}>
                  Password
                </Label>
                <Link
                  to="/academy/forgot-password"
                  className="text-[10px] font-bold uppercase tracking-[0.2em] hover:opacity-80"
                  style={{ color: COLORS.cyan }}
                  data-testid="lchaim-forgot-link"
                >
                  Forgot?
                </Link>
              </div>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="pr-10 border-0"
                  style={{ backgroundColor: COLORS.parchment, color: COLORS.darkBlue }}
                  data-testid="lchaim-login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full py-6 font-bold uppercase tracking-wider"
              style={{ backgroundColor: COLORS.red, color: COLORS.white }}
              data-testid="lchaim-login-submit"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Sign In'}
            </Button>
          </form>

          <div className="mt-6 pt-6 text-center text-sm" style={{ borderTop: `1px solid ${COLORS.cyan}20` }}>
            <span style={{ color: COLORS.gray }}>New to L'Chaim? </span>
            <Link
              to="/academy/lchaim#tiers"
              className="font-bold uppercase tracking-wider"
              style={{ color: COLORS.cyan }}
              data-testid="lchaim-signup-link"
            >
              Begin Your Journey
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LChaimLogin;
